import {useCallback, useEffect, useRef, useState} from 'react'
import {STORAGE_KEYS} from '@/shared/config'

const kindToStorageKey = (kind: 'audioinput' | 'videoinput'): string =>
  kind === 'audioinput' ? STORAGE_KEYS.AUDIO_DEVICE_ID : STORAGE_KEYS.VIDEO_DEVICE_ID

/**
 * @param onAutoSwitch - Called when the active device disappears and a new one
 *   is automatically selected (e.g. headphones disconnected). Use this to
 *   switch the actual media track to the replacement device.
 */
export function useMediaDevices(
  kind: 'audioinput' | 'videoinput',
  onAutoSwitch?: (deviceId: string) => void,
) {
  const storageKey = kindToStorageKey(kind)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(() =>
    typeof localStorage !== 'undefined' ? localStorage.getItem(storageKey) : null,
  )

  // Keep a stable ref so enumerate() doesn't need to list the callback as a dep.
  const onAutoSwitchRef = useRef(onAutoSwitch)
  onAutoSwitchRef.current = onAutoSwitch

  const enumerate = useCallback(async () => {
    const all = await navigator.mediaDevices.enumerateDevices()
    const filtered = all.filter(d => d.kind === kind)
    setDevices(filtered)

    // Auto-select first real device if nothing is saved yet or saved device is gone
    const savedId = localStorage.getItem(storageKey)
    const stillAvailable = filtered.some(d => d.deviceId === savedId)
    if (!stillAvailable && filtered.length > 0) {
      // Prefer a device with a real hardware ID over the virtual "default" entry
      const candidate = filtered.find(d => d.deviceId !== 'default') ?? filtered[0]
      if (candidate) {
        setSelectedDeviceId(candidate.deviceId)
        localStorage.setItem(storageKey, candidate.deviceId)
        // Notify caller so it can switch the actual media track to the new device.
        onAutoSwitchRef.current?.(candidate.deviceId)
      }
    }
  }, [kind, storageKey])

  useEffect(() => {
    void enumerate()
    navigator.mediaDevices.addEventListener('devicechange', enumerate)
    return () => navigator.mediaDevices.removeEventListener('devicechange', enumerate)
  }, [enumerate])

  const selectDevice = useCallback(
    (deviceId: string) => {
      setSelectedDeviceId(deviceId)
      localStorage.setItem(storageKey, deviceId)
    },
    [storageKey],
  )

  return {devices, selectedDeviceId, selectDevice, refreshDevices: enumerate}
}
